/**
 * SecondaryDataNotice -- loading / failure notice for SECONDARY data (FEAT-052 TASK-008,
 * design.md A). Never blocks the rest of the screen; never silent.
 *
 * - Loading: a polite live region mounted EMPTY from the first instant, text after 300 ms.
 * - Failure: role=alert with "Try again" (calls `onRetry ?? query.refetch`).
 * - 403/404: role=alert with a permission message and no retry.
 * - Nothing to show: renders nothing (no dead live region).
 */

import { useIntl } from 'react-intl';
import { Lock, WarningCircle } from '@phosphor-icons/react';
import { cn } from '@/utils/cn';
import { useSecondaryQueryState, type SecondaryQueryLike } from '@/hooks/useSecondaryQueryState';
import { Spinner } from './Spinner';
import { Button } from './Button';

export type SecondaryDataNoticeVariant = 'inline' | 'field' | 'block';

export interface SecondaryDataNoticeProps {
  query: SecondaryQueryLike;
  /** Human-readable name of the missing data with definite article, e.g. "a lista de equipes". */
  subject: string;
  variant?: SecondaryDataNoticeVariant;
  /** Replaces `query.refetch` (e.g. when two queries feed the same block). */
  onRetry?: () => void;
  /** Subject used in the permission message when it needs a different form than `subject`. */
  forbiddenSubject?: string;
  /** Id of the notice line, for the `aria-describedby` of the controlled field. */
  id?: string;
  className?: string;
}

const lineStyles: Record<SecondaryDataNoticeVariant, string> = {
  inline: 'flex items-center gap-1.5 text-xs text-text-secondary min-h-5',
  field: 'mt-1.5 flex items-center gap-1.5 text-xs text-text-secondary min-h-5',
  block:
    'flex min-h-24 flex-col items-center justify-center gap-2 rounded-lg border border-border-subtle bg-bg-secondary p-4 text-center text-sm text-text-secondary',
};

const iconSize: Record<SecondaryDataNoticeVariant, number> = { inline: 14, field: 14, block: 16 };

export function SecondaryDataNotice({
  query,
  subject,
  variant = 'inline',
  onRetry,
  forbiddenSubject,
  id,
  className,
}: SecondaryDataNoticeProps) {
  const intl = useIntl();
  const state = useSecondaryQueryState(query);
  const box = cn(lineStyles[variant], 'transition-opacity duration-150 ease-out', className);
  const size = iconSize[variant];

  if (state.isPending) {
    return (
      <div id={id} role="status" aria-live="polite" aria-busy="true" className={box}>
        {state.showLoading && (
          <>
            <span aria-hidden="true" className="inline-flex">
              <Spinner size="xs" className="text-text-tertiary" />
            </span>
            <span>
              {intl.formatMessage(
                { id: 'common.secondaryData.loading', defaultMessage: 'Carregando {subject}...' },
                { subject },
              )}
            </span>
          </>
        )}
      </div>
    );
  }

  if (!state.showError) return null;

  if (state.isForbidden) {
    return (
      <div id={id} role="alert" className={box}>
        <Lock size={size} aria-hidden="true" className="shrink-0 text-text-tertiary" />
        <span>
          {intl.formatMessage(
            {
              id: 'common.secondaryData.forbidden',
              defaultMessage: 'Você não tem permissão para ver {subject}.',
            },
            { subject: forbiddenSubject ?? subject },
          )}
        </span>
      </div>
    );
  }

  const handleRetry = onRetry ?? state.retry;
  const retryLabel = intl.formatMessage({
    id: 'common.secondaryData.retry',
    defaultMessage: 'Tentar novamente',
  });

  return (
    <div id={id} role="alert" className={box}>
      <WarningCircle size={size} weight="fill" aria-hidden="true" className="shrink-0 text-warning" />
      <span>
        {intl.formatMessage(
          { id: 'common.secondaryData.error', defaultMessage: 'Não foi possível carregar {subject}.' },
          { subject },
        )}
      </span>
      {variant === 'block' ? (
        <Button type="button" variant="ghost" size="sm" onClick={handleRetry}>
          {retryLabel}
        </Button>
      ) : (
        <button
          type="button"
          onClick={handleRetry}
          className="min-h-[var(--touch-target-min)] cursor-pointer text-text-accent underline underline-offset-2 hover:no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-focus lg:min-h-0"
        >
          {retryLabel}
        </button>
      )}
    </div>
  );
}

export default SecondaryDataNotice;
